import { useContext, useMemo } from 'react'

import useOfferList from 'hooks/Offers/useOfferList'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import { MALDIVES_BUNDLE_DEFAULT_NIGHTS, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { OFFER_TYPE_HOTEL } from 'constants/offer'
import Places from 'constants/places'
import config from 'constants/config'
import { FlightsClassTypes } from 'constants/flight'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import CurrencyContext from 'contexts/currencyContext'
import { useBundleFlightData } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useBundleFlightData'
import { useMaldivesDirectHotelStreamSearch } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useMaldivesDirectHotelStreamSearch'
import { filterDateRangesByNights, useSeatEligiblePassengers } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/utils'

interface Props {
  excludedIds?: Array<string>
}

interface MaldivesCharterOfferList extends App.OfferList {
  bundleFilters?: App.OfferListFilters
}

/**
 * Fetches Maldives charter flight bundle offers with pricing
 * Uses the campaign tag to filter offers that are part of the Maldives Direct program
 * Also fetches bundle pricing (hotel + flight) for the next available charter period
 */
export default function useMaldivesCharterRecommendedOfferList({ excludedIds }: Props): MaldivesCharterOfferList {
  const defaultRooms = useMemo(() => [config.search.defaultOccupants], [])
  const currency = useContext(CurrencyContext)
  const seatEligiblePassengers = useSeatEligiblePassengers(defaultRooms)

  const { dateRangesByMonth: allDateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: defaultRooms,
    fareClass: FlightsClassTypes.ECONOMY,
  })
  const dateRangesByMonth = useMemo(
    () => filterDateRangesByNights(allDateRangesByMonth, MALDIVES_BUNDLE_DEFAULT_NIGHTS),
    [allDateRangesByMonth],
  )
  const { flightDataByDate } = useBundleFlightData({
    rooms: defaultRooms,
    dateRangesByMonth,
    fareClass: FlightsClassTypes.ECONOMY,
    currency,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    seatEligiblePassengers,
  })

  // Filters for fetching offer list (without dates, for general listing)
  const listFilters = useMemo<App.OfferListFilters>(() => ({
    offerTypes: [OFFER_TYPE_HOTEL],
    destinationId: Places.Maldives.id,
    campaigns: [MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG],
    frontPageVisibleOnly: true,
    limit: 25,
  }), [])

  const { bundleFilters, canStreamHotelOffers } = useMaldivesDirectHotelStreamSearch({
    checkInDate: undefined,
    checkOutDate: undefined,
    nights: MALDIVES_BUNDLE_DEFAULT_NIGHTS,
    rooms: defaultRooms,
    fareClassForSearch: FlightsClassTypes.ECONOMY,
    holidayTypes: undefined,
    amenities: undefined,
    sortBy: undefined,
    flightDataByDate,
    isTourBundleMode: false,
  })

  const offerList = useOfferList(listFilters, { excludedIds })

  // Also fetch with bundle filters for pricing (this triggers bundle price calculation)
  useOfferList(bundleFilters, {
    disabled: !canStreamHotelOffers,
    minRequiredOffers: 1,
  })

  return {
    ...offerList,
    bundleFilters,
  }
}
